/**
 * Base64 encoding/decoding utilities with Unicode support
 * @module shared-cart/utils/base64
 */

/**
 * Decodes a Base64 string to a UTF-8 string
 * @param {string} base64 - Standard Base64 encoded string
 * @returns {string} Decoded UTF-8 string
 */
export function base64DecodeUnicode(base64: string): string {
  const binaryString = atob(base64);
  const bytes = Uint8Array.from(binaryString, (char) => char.codePointAt(0)!);
  return new TextDecoder().decode(bytes);
}

/**
 * Decodes a URL-safe Base64 string to a UTF-8 string
 * @param {string} str - URL-safe Base64 encoded string
 * @returns {string} Decoded UTF-8 string
 */
export function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  return base64DecodeUnicode(base64);
}

/**
 * Encodes a UTF-8 string to a Base64 string
 * @param {string} str - UTF-8 string to encode
 * @returns {string} Standard Base64 encoded string
 */
export function base64EncodeUnicode(str: string): string {
  const utf8Bytes = new TextEncoder().encode(str);
  const binaryString = Array.from(utf8Bytes, (byte) => String.fromCodePoint(byte)).join('');
  return btoa(binaryString);
}

/**
 * Encodes a UTF-8 string to a URL-safe Base64 string
 * @param {string} str - UTF-8 string to encode
 * @returns {string} URL-safe Base64 encoded string
 */
export function base64UrlEncode(str: string): string {
  const base64 = base64EncodeUnicode(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
